import type { ReactNode } from "react";
import { Controller, FormProvider, type UseFormReturn } from "react-hook-form";
import { Save, Loader2 } from "lucide-react";
import { ClienteSegmento, ClienteSegmentoMap } from "@workflow/types";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FormInput } from "@/components/campos-formulario/novos/FormInput";
import { FormSelect } from "@/components/campos-formulario/novos/FormSelect";
import { CLIENTE_SEGMENTO_ICON_MAP } from "@/constants/clienteSegmento";
import type { ClienteFormValues } from "@/hooks/clientes/use-cliente-form";

const segmentoOptions = Object.values(ClienteSegmento).map((segmento) => {
  const Icon = CLIENTE_SEGMENTO_ICON_MAP[segmento];
  return {
    value: segmento,
    label: ClienteSegmentoMap[segmento],
    icon: <Icon className="h-4 w-4" />,
  };
});

interface ClienteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<ClienteFormValues>;
  isSubmitting: boolean;
  onSubmit: () => void;
  icon: ReactNode;
  title: string;
  subtitle: ReactNode;
  submitLabel: string;
  submittingLabel: string;
  isCreate?: boolean;
}

export function ClienteFormDialog({
  open,
  onOpenChange,
  form,
  isSubmitting,
  onSubmit,
  icon,
  title,
  subtitle,
  submitLabel,
  submittingLabel,
  isCreate = false,
}: ClienteFormDialogProps) {
  const {
    control,
    formState: { errors, isValid },
  } = form;

  return (
    <Dialog open={open} onOpenChange={(val) => !isSubmitting && onOpenChange(val)}>
      <DialogContent className="flex max-h-[92dvh] flex-col gap-0 overflow-hidden rounded-xl border-border bg-background p-0 shadow-2xl sm:max-w-[520px] sm:rounded-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 items-center gap-3 border-b bg-card px-5 py-4 sm:px-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-600/10 text-purple-600">
            {icon}
          </div>
          <div className="min-w-0">
            <h2 className="mb-1 text-base font-bold leading-none tracking-tight sm:text-lg">
              {title}
            </h2>
            <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>

        <FormProvider {...form}>
          <form
            onSubmit={onSubmit}
            className="flex min-h-0 flex-1 flex-col"
            autoComplete="off"
            noValidate
          >
            <div className="custom-scrollbar flex-1 space-y-5 overflow-y-auto p-5 sm:p-6">
              <Controller
                name="nome"
                control={control}
                render={({ field }) => (
                  <FormInput
                    id="nome"
                    label="Nome do cliente"
                    required
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="Ex: Coca-Cola Brasil"
                    error={errors.nome?.message}
                  />
                )}
              />

              <Controller
                name="segmento"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    id="segmento"
                    label="Segmento (opcional)"
                    value={field.value ?? ""}
                    onChange={(value) => field.onChange(value)}
                    onBlur={field.onBlur}
                    options={segmentoOptions}
                    placeholder="Selecione o segmento..."
                    allowClear
                    error={errors.segmento?.message}
                  />
                )}
              />

              <Controller
                name="grupo"
                control={control}
                render={({ field }) => (
                  <FormInput
                    id="grupo"
                    label="Grupo (opcional)"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="Ex: Unilever"
                    helperText={
                      isCreate
                        ? "Usado para vincular as filiais deste cliente automaticamente."
                        : undefined
                    }
                    error={errors.grupo?.message}
                  />
                )}
              />

              <Controller
                name="descricao"
                control={control}
                render={({ field }) => (
                  <FormInput
                    id="descricao"
                    label="O que o cliente faz (opcional)"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="Ex: fastfood, aluguel de carros"
                    error={errors.descricao?.message}
                  />
                )}
              />
            </div>

            <div className="flex shrink-0 flex-col-reverse justify-end gap-3 border-t bg-muted/10 p-5 sm:flex-row sm:p-6">
              <Button
                type="button"
                variant="outline"
                className="w-full bg-background shadow-sm hover:bg-muted sm:w-32"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                className="w-full gap-2 bg-purple-600 font-bold text-white shadow-sm hover:bg-purple-700 sm:w-auto sm:min-w-40"
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> {submittingLabel}
                  </>
                ) : (
                  <>
                    {submitLabel}
                    <Save className="h-4 w-4 opacity-70" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
